import React from "react";
import { cn } from "@/lib/utils";
import { Loader2, Info, Sparkles } from "lucide-react";
import { toast as sonnerToast } from "sonner";

/* ---------------- Toast helper (parity with mobile useToast) ---------------- */
const toastApi = {
  show: (message, kind = "info") => {
    if (kind === "error") sonnerToast.error(message);
    else if (kind === "success") sonnerToast.success(message);
    else sonnerToast(message);
  },
};
export function useToast() {
  return toastApi;
}

/* ---------------- Text ---------------- */
export function Kicker({ children, className }) {
  return <div className={cn("earnos-kicker", className)}>{children}</div>;
}
export function SectionTitle({ children, className }) {
  return <h3 className={cn("text-lg font-bold text-ink", className)}>{children}</h3>;
}
export function PageTitle({ children, className }) {
  return <h1 className={cn("text-3xl sm:text-4xl font-extrabold tracking-tight text-ink", className)}>{children}</h1>;
}

/* ---------------- Card ---------------- */
export function Card({ children, className, testID, ...rest }) {
  return (
    <div
      data-testid={testID}
      className={cn("rounded-2xl border border-line bg-surface-2 p-5", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function GlassCard({ children, className, gradient = "from-brand to-brand-violet", testID }) {
  return (
    <div
      data-testid={testID}
      className={cn("rounded-2xl p-6 overflow-hidden bg-gradient-to-br text-white", gradient, className)}
    >
      {children}
    </div>
  );
}

/* ---------------- Button ---------------- */
export function Button({
  title,
  children,
  onClick,
  variant = "primary",
  loading = false,
  disabled = false,
  icon: Icon,
  className,
  testID,
  small = false,
  type = "button",
  full = false,
}) {
  const isDisabled = disabled || loading;
  const base = cn(
    "inline-flex items-center justify-center gap-2 rounded-full font-bold transition-all duration-200 select-none",
    small ? "h-10 px-4 text-sm" : "h-[52px] px-6 text-base",
    full && "w-full",
    isDisabled ? "opacity-50 cursor-not-allowed" : "hover:brightness-110 active:scale-[0.98] cursor-pointer",
  );
  const variants = {
    primary: "bg-gradient-to-br from-brand-light to-brand text-white shadow-lg shadow-brand/30",
    secondary: "bg-surface-3 border border-line-strong text-ink",
    ghost: "bg-transparent text-ink hover:bg-surface-3",
    danger: "bg-danger text-white",
  };
  return (
    <button
      type={type}
      data-testid={testID}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-busy={loading}
      aria-label={loading && typeof title === "string" ? title : undefined}
      className={cn(base, variants[variant], className)}
    >
      {loading ? (
        <Loader2 className="animate-spin" size={18} />
      ) : (
        <>
          {Icon ? <Icon size={18} /> : null}
          {title || children}
        </>
      )}
    </button>
  );
}

/* ---------------- Chip ---------------- */
export function Chip({ label, selected, onClick, testID }) {
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      aria-pressed={Boolean(selected)}
      className={cn(
        "h-10 px-4 rounded-full border text-sm font-semibold transition-all duration-150 active:scale-95",
        selected
          ? "bg-brand border-brand-light text-white"
          : "bg-surface-3 border-line text-ink-3 hover:border-line-strong",
      )}
    >
      {label}
    </button>
  );
}

/* ---------------- Pill ---------------- */
export function Pill({ label, color = "#6366F1", className }) {
  return (
    <span
      className={cn("inline-block px-2.5 py-1 rounded-full text-xs font-bold border", className)}
      style={{ color, backgroundColor: color + "22", borderColor: color + "55" }}
    >
      {label}
    </span>
  );
}

/* ---------------- Input / Textarea ---------------- */
export function Input({ label, className, testID, id, ...props }) {
  const generatedId = React.useId();
  const fieldId = id || generatedId;
  return (
    <div className="mb-4">
      {label ? <label htmlFor={fieldId} className="block text-xs font-semibold text-ink-2 mb-2">{label}</label> : null}
      <input
        id={fieldId}
        data-testid={testID}
        className={cn(
          "w-full min-h-[52px] rounded-xl border border-line bg-surface-2 px-4 py-3 text-base text-ink placeholder:text-ink-2 outline-none focus:border-brand-light transition-colors",
          className,
        )}
        {...props}
      />
    </div>
  );
}

export function Textarea({ label, className, testID, id, ...props }) {
  const generatedId = React.useId();
  const fieldId = id || generatedId;
  return (
    <div className="mb-4">
      {label ? <label htmlFor={fieldId} className="block text-xs font-semibold text-ink-2 mb-2">{label}</label> : null}
      <textarea
        id={fieldId}
        data-testid={testID}
        className={cn(
          "w-full rounded-xl border border-line bg-surface-2 px-4 py-3 text-base text-ink placeholder:text-ink-2 outline-none focus:border-brand-light transition-colors resize-y",
          className,
        )}
        {...props}
      />
    </div>
  );
}

/* ---------------- Disclaimer ---------------- */
export function Disclaimer({ text }) {
  if (!text) return null;
  return (
    <div className="flex items-start gap-1.5 mt-3">
      <Info size={14} className="text-ink-2 mt-0.5 shrink-0" />
      <p className="text-xs text-ink-2 leading-tight">{text}</p>
    </div>
  );
}

/* ---------------- EmptyState ---------------- */
export function EmptyState({ icon: Icon = Sparkles, title, message, actionTitle, onAction, testID }) {
  return (
    <div data-testid={testID} className="flex flex-col items-center justify-center text-center py-16 px-6">
      <div className="w-[72px] h-[72px] rounded-full bg-brand/20 flex items-center justify-center mb-4">
        <Icon size={30} className="text-brand-light" />
      </div>
      <h3 className="text-xl font-bold text-ink mb-1">{title}</h3>
      <p className="text-base text-ink-2 max-w-md leading-relaxed">{message}</p>
      {actionTitle ? (
        <Button title={actionTitle} onClick={onAction} testID="empty-action-button" className="mt-5 min-w-[220px]" />
      ) : null}
    </div>
  );
}

/* ---------------- Spinner ---------------- */
export function Spinner({ className }) {
  return (
    <div className={cn("flex items-center justify-center", className)}>
      <Loader2 className="animate-spin text-brand-light" size={28} />
    </div>
  );
}

/* ---------------- ProgressBar ---------------- */
export function ProgressBar({ value = 0, className, barClassName = "bg-brand", trackClassName = "bg-surface-3" }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className={cn("h-2 rounded-full overflow-hidden", trackClassName, className)}>
      <div className={cn("h-full rounded-full transition-all duration-500", barClassName)} style={{ width: `${pct}%` }} />
    </div>
  );
}
